import React from 'react'
import { lightTheme } from '@/customTheme'
import { hexToRgba } from '@/scenes/engine/utils/utils'
import Tooltip from '@/components/Tooltip'
import { Z_INDEX } from '@/constants/z-index'
import EditWithAIFloatingLoader from './components/EditWithAIFloatingLoader'
import EditWithAIPostGeneration from './components/EditWithAIPostGeneration'

export type EditWithAIViewProps = {
  // Sizing
  containerWidth: number

  // State
  submittedPrompt: string
  isDisabled: boolean
  isHiddenPreserveSpace: boolean
  isProcessing: boolean
  loaderLabel: string
  showTryAgainGroup: boolean
  /**
   * Localized text for the disabled-state overlay tooltip. Omit to render no
   * overlay. The caller owns the disabled-state policy (e.g. Editor's
   * multi-image vs. transparency messaging); this view has no scene knowledge.
   */
  disabledTooltipText?: string

  // Delegates
  onTryAgain: () => void
  onClosePostGeneration: () => void
  onEditPrompt: () => void

  // Slots
  /** Suggestions bar floating above the box. */
  suggestions?: React.ReactNode
  /** Prompt input inside the box. */
  textbox?: React.ReactNode
  /** Below-box content (extra controls, reserved for future use). */
  footer?: React.ReactNode
}

export function EditWithAIView({
  containerWidth,
  submittedPrompt,
  isDisabled,
  isHiddenPreserveSpace,
  isProcessing,
  loaderLabel,
  showTryAgainGroup,
  disabledTooltipText,
  onTryAgain,
  onClosePostGeneration,
  onEditPrompt,
  suggestions,
  textbox,
  footer,
}: EditWithAIViewProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      {/* Floating layers: loader, suggestions, post-generation */}
      <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
        <div style={{ position: 'absolute', bottom: 0 }}>
          <EditWithAIFloatingLoader show={isProcessing} loaderLabel={loaderLabel} maxWidth={containerWidth} />
        </div>
        {suggestions && (
          <div style={{ position: 'absolute', bottom: 0 }}>
            {suggestions}
          </div>
        )}
        <div style={{ position: 'absolute', bottom: 0 }}>
          <EditWithAIPostGeneration
            prompt={submittedPrompt}
            onEditClick={onEditPrompt}
            onRegenerateClick={onTryAgain}
            onCloseClick={onClosePostGeneration}
            isVisible={showTryAgainGroup && !isDisabled && !isProcessing}
            maxWidth={containerWidth}
          />
        </div>
      </div>

      {/* Main box */}
      <div
        id="edit-with-ai"
        style={{
          position: 'relative',
          height: 'fit-content',
          width: `${containerWidth}px`,
          minWidth: '256px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: `0 0 6px 0 ${hexToRgba(lightTheme.colors.black, 0.08)}`,
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
          opacity: isHiddenPreserveSpace ? 0 : 1,
        }}
      >
        {textbox}
        {disabledTooltipText && (
          <Tooltip
            info={{
              content: disabledTooltipText,
              placement: 'top',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: Z_INDEX.EDIT_WITH_AI,
              }}
            />
          </Tooltip>
        )}
      </div>

      {/* Footer slot */}
      {footer}
    </div>
  )
}

export default EditWithAIView
